import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { LucideCheck, LucideCopy, LucideCreditCard, LucideLink } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { authService } from "@/core/auth/services/authService";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { useClient } from "@/features/clients/hooks/clientQueries";
import type { Client } from "@/features/clients/types/clientTypes";
import { getClientName } from "@/features/clients/utils/clientDisplay";
import { Button } from "@/shared/components/shadcn/Button";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { IconBadge } from "@/shared/components/ui/IconBadge";
import { InfoRow } from "@/shared/components/ui/InfoRow";
import { InitialsAvatar } from "@/shared/components/ui/InitialsAvatar";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { Section } from "@/shared/components/ui/Section";
import { SectionAction } from "@/shared/components/ui/SectionAction";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { useWorkspaceOperation } from "@/shared/hooks/useWorkspaceOperation";
import { CardSetupFlow } from "../components/CardSetupFlow";
import { paymentMethodKeys } from "../hooks/paymentMethodQueries";
import { paymentMethodApiService } from "../services/paymentMethodApiService";
import { fromSetupSessionDtoToSetupSession, fromSetupStatusDtoToSetupStatus, type SetupSession } from "../types/paymentMethodTypes";

type Mode = "manual" | "link";

function AddCard({ businessId, client, userId }: { businessId: string; client: Client; userId: string }) {
  const clientId = client.id;
  const capture = useWorkspaceOperation();
  const { goToClientDetails } = useAppNavigation();
  const queryClient = useQueryClient();
  const [session, setSession] = useState<SetupSession>();
  const [mode, setMode] = useState<Mode>();
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [copied, setCopied] = useState(false);
  const pending = useRef<AbortController | null>(null);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => { pending.current?.abort(); clearTimeout(copyTimer.current); }, []);

  async function start(nextMode: Mode) {
    if (pending.current) return;
    const current = new AbortController();
    pending.current = current;
    const isCurrent = capture();
    setMode(nextMode);
    setBusy(true);
    setError("");
    try {
      const result = fromSetupSessionDtoToSetupSession(
        await paymentMethodApiService.createSetupSession(businessId, clientId, current.signal));
      if (isCurrent() && !current.signal.aborted) setSession(result);
    } catch {
      if (isCurrent() && !current.signal.aborted) setError("Couldn't create card setup. Check the business's Stripe connection and try again.");
    } finally {
      if (pending.current === current) pending.current = null;
      if (!current.signal.aborted) {
        setBusy(false);
        if (!isCurrent()) setError("The workspace changed while preparing setup. Verify the workspace and try again.");
      }
    }
  }
  function change() {
    pending.current?.abort();
    pending.current = null;
    clearTimeout(copyTimer.current);
    setSession(undefined);
    setMode(undefined);
    setBusy(false);
    setChecking(false);
    setError("");
    setNotice("");
    setCopied(false);
  }
  function done() {
    void queryClient.invalidateQueries({ queryKey: paymentMethodKeys.client(businessId, clientId, userId) });
    goToClientDetails(clientId, "finish");
  }
  async function copy() {
    if (!session) return;
    const isCurrent = capture();
    clearTimeout(copyTimer.current);
    setCopied(false);
    setError("");
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(session.url);
      if (!isCurrent()) return;
      setCopied(true);
      copyTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      if (isCurrent()) setError("Couldn't copy the link. Ask the customer to scan the code, or try Copy again.");
    }
  }
  async function checkCompleted() {
    if (!session || checking) return;
    const token = session.token;
    const isCurrent = capture();
    setChecking(true);
    setError("");
    setNotice("");
    try {
      const result = fromSetupStatusDtoToSetupStatus(await paymentMethodApiService.status(token));
      if (!isCurrent()) return;
      if (result.status === "succeeded") done();
      else if (result.status === "canceled") setError("This card setup was canceled. Choose Change to start again.");
      else setNotice("Not completed yet.");
    } catch {
      if (isCurrent()) setError("Couldn't check whether the card was saved. Try again.");
    } finally {
      setChecking(false);
    }
  }

  const customer = <InfoRow title={getClientName(client)} subtitle={client.email || undefined} density="compact"
    icon={<InitialsAvatar firstName={client.firstName || ""} lastName={client.lastName || ""} />} />;
  if (!mode) return <div className="space-y-[26px]">
    {customer}
    <Section title="How would you like to add a card?">
      <div className="space-y-2">
        <InfoRow title="Enter card manually" subtitle="Type in the card details yourself"
          icon={<IconBadge tone="primary"><LucideCreditCard aria-hidden="true" /></IconBadge>} onClick={() => void start("manual")} />
        <InfoRow title="Send a secure link" subtitle="The customer adds it on their phone"
          icon={<IconBadge tone="ai"><LucideLink aria-hidden="true" /></IconBadge>} onClick={() => void start("link")} />
      </div>
    </Section>
  </div>;
  return <div className="space-y-[26px]">
    {customer}
    <Section title={mode === "manual" ? "Enter card manually" : "Secure link"} card
      action={<SectionAction aria-label="Choose a different way" onClick={change}>Change</SectionAction>}>
      {busy ? <LoadingSpinner message="Creating secure setup..." /> : !session ? null
        : mode === "manual" && authService.kind !== "host" ? <CardSetupFlow key={session.id} token={session.token} onDone={done} staff />
        : <div className="space-y-4">
          <div className="mx-auto w-fit rounded-2xl border border-border bg-white p-3">
            <QRCodeSVG value={session.url} size={192} title="Secure card setup link" />
          </div>
          <InfoRow topCaption="Secure setup link" title={<span className="block truncate">{session.url}</span>} density="compact"
            icon={<LucideLink className="size-5 text-muted-foreground" aria-hidden="true" />}
            actions={<Button variant="quiet" aria-label="Copy secure setup link" onClick={() => void copy()}>
              {copied ? <LucideCheck aria-hidden="true" /> : <LucideCopy aria-hidden="true" />}
              <span className="sr-only sm:not-sr-only">{copied ? "Copied" : "Copy"}</span>
            </Button>} />
          <p className="text-xs text-muted-foreground">Expires {session.expiresAt.toLocaleString()}.</p>
          <p role="status" className="sr-only">{copied ? "Link copied." : ""}</p>
          {notice && <p role="status" className="text-sm">{notice}</p>}
          <Button className="w-full" disabled={checking} onClick={() => void checkCompleted()}>
            {checking ? "Checking..." : "Check if completed"}
          </Button>
        </div>}
      {error && <ErrorState message={error} onRetry={!session && !busy ? () => void start(mode) : undefined} />}
    </Section>
  </div>;
}

export function PaymentMethodAddPage() {
  const { clientId = "" } = useParams();
  const { business, businessId } = useCurrentBusiness();
  const { user } = useAuth();
  const clientQuery = useClient(clientId);
  const authorized = business && businessId && user && business.ownerId === user.id &&
    clientQuery.data?.businessId === businessId;
  return <Page>
    <meta name="referrer" content="no-referrer" />
    <PageHeader title="Add payment method" />
    <PageContent>{clientQuery.isPending ? <LoadingSpinner message="Loading customer..." /> :
      clientQuery.error ? <ErrorState message={clientQuery.error.message} onRetry={() => void clientQuery.refetch()} /> :
      authorized ? <AddCard key={`${user.id}:${businessId}:${clientId}`} businessId={businessId} client={clientQuery.data} userId={user.id} /> :
        <ErrorState message="Only the business owner can manage this customer's payment methods." />}
    </PageContent>
  </Page>;
}
